import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { getAllPosts } from "@/lib/blog";
import { useLanguage } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const Blog = () => {
  const { language, t } = useLanguage();
  const [hideForeign, setHideForeign] = useState(false);

  const groupedPosts = useMemo(() => {
    // 1. Get ALL posts (no language filter initially)
    const allPosts = getAllPosts();

    // 2. Group by specific Date (YYYY-MM-DD) to identify duplicates/translations
    const postsByDate: Record<string, typeof allPosts> = {};
    allPosts.forEach(post => {
      if (!postsByDate[post.date]) {
        postsByDate[post.date] = [];
      }
      postsByDate[post.date].push(post);
    });

    // 3. Process duplicates and flatten
    let processedPosts: typeof allPosts = [];
    Object.entries(postsByDate).forEach(([date, posts]) => {
      if (posts.length > 1) {
        // Multiple posts on same date = likely translations
        // Check if we have a post in the selected language
        const preferredPost = posts.find(p => p.language === language);
        if (preferredPost) {
          processedPosts.push(preferredPost);
        } else {
          // If no post in selected language, keep all (or handle as needed)
          processedPosts.push(...posts);
        }
      } else {
        processedPosts.push(...posts);
      }
    });

    // 4. Apply "Hide foreign" filter if enabled
    if (hideForeign) {
      processedPosts = processedPosts.filter(post => post.language === language);
    }

    // 5. Group by Year for display
    const groups = processedPosts.reduce((acc: Record<string, typeof processedPosts>, post) => {
      // Optimization: substring is faster than Date parsing
      const year = post.date.substring(0, 4);
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(post);
      return acc;
    }, {});

    // Convert to array and sort by year (newest first)
    return Object.entries(groups)
      .map(([year, posts]) => ({
        year,
        // Optimization: string comparison is faster for ISO dates
        posts: posts.sort((a, b) => b.date.localeCompare(a.date)),
        isNew: parseInt(year) >= 2025
      }))
      .sort((a, b) => parseInt(b.year) - parseInt(a.year));
  }, [language, hideForeign]);

  return (
    <main id="main-content" className="min-h-screen">
      <SEO
        title={t("seo.blog.title")}
        description={t("seo.blog.description")}
        keywords={t("seo.blog.keywords").split(", ")}
      />
      <div className="container mx-auto px-2 sm:px-4 py-12">
        <Breadcrumbs />

        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold animate-fade-in">
                {language === 'en' ? 'Blog' : 'Blog'}
              </h1>
              <p className="text-xl text-muted-foreground mt-2 animate-fade-in" style={{ animationDelay: "100ms" }}>
                {language === 'en'
                  ? 'Insights and practical ideas on change, leadership, and innovation in organizations.'
                  : 'Einsichten und praktische Ideen zu Wandel, Führung und Innovation in Organisationen.'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 animate-fade-in" style={{ animationDelay: "150ms" }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHideForeign(!hideForeign)}
                className="text-xs text-muted-foreground hover:text-primary"
              >
                {hideForeign
                  ? (language === 'en' ? 'Show all languages' : 'Alle Sprachen anzeigen')
                  : (language === 'en' ? 'Hide German articles' : 'Englische Artikel ausblenden')}
              </Button>
            </div>
          </div>

          {groupedPosts.length === 0 ? (
            <div className="text-center py-12 animate-fade-in">
              <p className="text-muted-foreground text-lg">
                {language === 'en'
                  ? 'No blog posts found.'
                  : 'Keine Blogbeiträge gefunden.'}
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {groupedPosts.map((group, groupIndex) => (
                <div key={group.year} className="space-y-6">
                  {group.isNew && groupIndex > 0 && (
                    <div className="relative my-8">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          {language === 'en' ? 'New Perspectives' : 'Neue Perspektiven'}
                        </span>
                      </div>
                    </div>
                  )}

                  <h2 className="text-2xl font-bold mt-8 mb-4">
                    {group.year}
                    {group.isNew && (
                      <span className="ml-3 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        {language === 'en' ? 'New' : 'Neu'}
                      </span>
                    )}
                  </h2>

                  <div className="space-y-6">
                    {group.posts.map((post, index) => (
                      <article
                        key={post.slug}
                        className="block group relative"
                      >
                        <div
                          className={`relative border border-primary/20 rounded-lg bg-card/50 backdrop-blur-sm hover:bg-card/80 hover:border-primary/40 transition-all duration-300 hover:scale-[1.02] animate-scale-in flex flex-col md:flex-row overflow-hidden`}
                          style={{ animationDelay: `${200 + index * 50}ms` }}
                        >
                          {post.image && (
                            <div className="hidden md:block w-1/3 shrink-0 relative">
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                            </div>
                          )}
                          <div className={`p-6 flex flex-col justify-between ${post.image ? 'md:w-2/3' : 'w-full'}`}>
                            <div>
                              <div className="flex justify-between items-start gap-4 mb-3">
                                <h3 className="text-2xl font-bold group-hover:text-primary transition-colors">
                                  <Link
                                    to={`/blog/${post.slug}`}
                                    title={post.title}
                                    className="static before:absolute before:inset-0 outline-none focus:ring-2 focus:ring-primary rounded-lg"
                                  >
                                    {post.title}
                                  </Link>
                                </h3>
                                <div className="flex gap-2 shrink-0 relative z-10">
                                  {post.isLinkedInPost && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                      LinkedIn
                                    </span>
                                  )}
                                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                                    {post.language.toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <p className="text-muted-foreground mb-4 relative z-10 pointer-events-none">
                                {post.excerpt}
                              </p>
                            </div>
                            <div className="flex justify-between items-center text-sm text-muted-foreground mt-auto relative z-10">
                              <time className="pointer-events-none" suppressHydrationWarning>
                                {new Date(post.date).toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </time>
                              {post.isLinkedInPost && post.linkedinUrl && (
                                <a
                                  href={post.linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline flex items-center gap-1 cursor-pointer text-sm"
                                  onClick={(e) => e.stopPropagation()}
                                  title={language === 'en' ? 'View on LinkedIn' : 'Auf LinkedIn ansehen'}
                                >
                                  {language === 'en' ? 'View on LinkedIn' : 'Auf LinkedIn ansehen'}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main >
  );
};

export default Blog;
